import { ComponentProps, ReactNode } from 'react';
import { VariantProps } from 'tailwind-variants';
import { Tooltip } from 'react-tooltip';
import { getFirstViewedLabel } from '../../../utils/dataTools';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Typography } from '../../../../core/components/molecules/Typography';
import {
  IResponseFavorite,
  useContentFavorite,
} from '../../../../modules/Disciplines/hooks/useContentFavorite';
import { Colors } from '../../../models/disciplines';
import { Tag } from '../../atoms/Tag';
import { ContentWrapper, IconDone, SubjectTag } from './variants';

interface IContentCard extends VariantProps<typeof ContentWrapper> {
  id: string;
  title?: string;
  slug?: string;
  content: string;
  subject?: string;
  children?: ReactNode;
  showDone?: boolean;
  done?: boolean;
  firstViewedAt?: string | null;
  showFavorite?: boolean;
  favorite?: boolean;
  onFavorite?: () => void;
  iconColor?: string;
  discipline?: string;
  disciplineSlug?: string;
  hasCallback?: boolean;
  updateList?: boolean;
  compactText?: boolean;
  icon: React.ComponentType<ComponentProps<'svg'>> | string;
  action?: () => void;
  callback?: (data: IResponseFavorite) => void;
  onDone?: (id: string, slug: string) => void;
  doneLoading?: boolean;
  locked?: boolean;
  freemiumUnlocked?: boolean;
}

export default function ContentCard({
  color = 'default',
  slug,
  title,
  content,
  children,
  discipline,
  disciplineSlug,
  subject,
  icon,
  done,
  firstViewedAt,
  showDone = true,
  favorite,
  onFavorite,
  showFavorite = true,
  action,
  id,
  iconColor = 'text-brand-color-secondary-pure',
  hasCallback,
  callback,
  onDone,
  doneLoading,
  selected,
  compactText = false,
  locked = false,
  freemiumUnlocked = false,
}: IContentCard) {
  const { mutate, isLoading } = useContentFavorite(hasCallback || false, callback);

  // Tooltip com a data em que o conteúdo foi visto pela primeira vez.
  const firstViewedLabel = getFirstViewedLabel(firstViewedAt);

  const favoriteAction = () => {
    if (
      content === 'videoaula' ||
      content === 'lesson' ||
      content === 'Videoaula'
    ) {
      mutate({
        category: 'lesson',
        targetId: id,
        targetType: 'Lesson',
      });
    }

    if (
      content === 'materiais' ||
      content === 'workbook' ||
      content === 'Material'
    ) {
      mutate({
        category: 'apostila',
        targetId: id,
        targetType: 'Material',
      });
    }

    if (content === 'mindmap') {
      mutate({
        category: 'mapa_mental',
        targetId: id,
        targetType: 'Material',
      });
    }

    if (content === 'podcast' || content === 'Podcast' || content === 'Audio') {
      mutate({
        category: 'audio',
        targetId: id,
        targetType: 'Material',
      });
    }

    if (
      content === 'Exercício' ||
      content === 'exercise' ||
      content === 'Exercicío'
    ) {
      mutate({
        category: 'exercise',
        targetId: id,
        targetType: 'Exercise',
      });
    }

    if (content === 'Live') {
      mutate({
        category: 'live',
        targetId: id,
        targetType: 'Live',
      });
    }
  };

  function getIcon() {
    if (typeof icon === 'string') {
      return (
        <Icon
          Icon={icon}
          type='material'
          className={`h-5 w-5 text-lg text-brand-color-secondary-pure ${
            color && color !== 'default'
              ? 'transition delay-100 ease-in-out group-hover:text-card-color-default'
              : ''
          }`}
        />
      );
    }
    return (
      <Icon
        Icon={icon}
        type='svg'
        className={`h-5 w-5 text-lg text-brand-color-secondary-pure ${
          color && color !== 'default'
            ? 'transition delay-100 ease-in-out group-hover:text-card-color-default'
            : ''
        }`}
      />
    );
  }

  function getDoneIcon() {
    if (['Exercício', 'exercise', 'Exercicío'].includes(content)) {
      if (done === undefined) {
        return (
          <Icon
            id={`check-icon-undefined`}
            Icon='check_circle'
            variant='rounded'
            type='material'
            className={IconDone({ type: 'unseen' })}
          />
        );
      }

      return (
        <Icon
          id={`check-icon-${done ? 'done' : 'íncorrect'}`}
          Icon={done ? 'check_circle' : 'cancel'}
          variant='rounded'
          type='material'
          className={IconDone({ type: done ? 'done' : 'incorrect' })}
        />
      );
    }

    return (
      <Icon
        id={`check-icon-${done ? 'done' : 'unseen'}`}
        Icon='check_circle'
        variant='rounded'
        type='material'
        className={IconDone({ type: done ? 'done' : 'unseen' })}
      />
    );
  }

  return (
    <div className={ContentWrapper({ color, selected })}>
      <div
        className='group flex w-full flex-col gap-sm'
        onClick={() => (action ? action() : {})}
      >
        <div
          className={`flex flex-row items-center justify-between ${title ? '' : 'mb-3'}`}
        >
          <div className='flex h-[16px] w-fit flex-row items-center gap-sm'>
            {getIcon()}
            <Typography
              type='titleSection'
              size='h6'
              className={`uppercase ${
                color && color !== 'default'
                  ? 'transition delay-100 ease-in-out group-hover:text-card-color-default'
                  : ''
              }`}
            >
              {content}
            </Typography>
          </div>

          {discipline && disciplineSlug && (
            <Tag
              variant={disciplineSlug as Colors}
              className='flex flex-row items-center gap-sm px-2 text-[#fff]'
            >
              {discipline}
            </Tag>
          )}
          {subject && (
            <Tag
              variant={color as Colors}
              className={SubjectTag({ color })}
              title={subject}
            >
              <Typography
                size='small'
                className='line-clamp-1 text-mono-color-white transition delay-100 ease-in-out group-hover:text-card-color-negative'
              >
                {subject}
              </Typography>
            </Tag>
          )}
        </div>
        {title && (
          <Typography
            type='title'
            size={compactText ? 'h5' : 'h4'}
            className={`${
              color && color !== 'default'
                ? 'transition delay-100 ease-in-out group-hover:text-card-color-default'
                : ''
            }`}
          >
            {title}
          </Typography>
        )}
        {children}
      </div>
      {locked ? (
        <div className='flex w-fit flex-col items-center justify-center'>
          <Icon
            id='lock-icon'
            Icon='lock'
            variant='rounded'
            type='material'
            className='text-xxl filled text-mono-color-medium'
          />
        </div>
      ) : (showDone || showFavorite) && (
        <div className='flex w-fit flex-col items-end justify-between'>
          {showDone && (
            <>
              <Button
                variant='text'
                onClick={() => onDone && onDone(id, slug || "")}
                className='p-0'
                loading={doneLoading ?? isLoading}
                data-tooltip-id={firstViewedLabel ? `first-viewed-${id}` : undefined}
                data-tooltip-content={firstViewedLabel ?? undefined}
              >
                {getDoneIcon()}
              </Button>
              {firstViewedLabel && (
                <Tooltip
                  id={`first-viewed-${id}`}
                  place='top'
                  opacity={1}
                  style={{
                    background: '#2E3847',
                    color: '#fff',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    fontSize: '12px',
                    zIndex: 20,
                  }}
                />
              )}
            </>
          )}
          {showFavorite &&
            (freemiumUnlocked ? (
              <Icon
                id='freemium-unlocked-icon'
                Icon='lock_open'
                variant='rounded'
                type='material'
                className='text-xxl filled text-feedback-color-success-pure'
              />
            ) : (
              <Button
                iconId={`favorite-icon-${favorite ? 'favorite' : 'not-favorite'}`}
                variant='text'
                onClick={() => (onFavorite ? onFavorite() : favoriteAction())}
                className='p-0'
                loading={isLoading}
                LeftIcon={'star'}
                iconVariant='rounded'
                classNameIcon={`${
                  !favorite ? 'text-mono-color-medium' : iconColor
                } filled`}
                iconType='material'
                onlyIcon
              />
            ))}
        </div>
      )}
    </div>
  );
}
